import * as Colors from '@brightlayer-ui/colors';
import { ListItemTag } from '@brightlayer-ui/react-components';
import { Business } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SelectableDropdownListItem } from '../../components/SelectableDropdownListItem';
import { OrganizationStatus, UserRole } from '../../services/user/types';
import { OrganizationId } from '../../types/idFlavors';
import { roleText } from '../../utils';
import { BLUISH_WHITE, GREENISH_WHITE } from '../../values/color';

const styles = {
    list: {
        marginLeft: '25px',
        marginRight: '25px',
    },
    leftComponent: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: Colors.white[400],
        padding: 1,
        borderRadius: 5,
    },
};

export type ListOrganizationsProps = {
    organizations: Array<{
        organizationName: string;
        organizationId: OrganizationId;
        role: UserRole;
        organizationStatus: OrganizationStatus;
    }>;
};

export const ListOrganizations: React.FC<ListOrganizationsProps> = (props) => {
    const { organizations } = props;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const filteredAndSortedOrganizations = organizations
        .filter((org) => org.organizationStatus !== OrganizationStatus.REJECTED)
        .sort((a, b) => a.organizationName.localeCompare(b.organizationName));

    const getTagStyles = (label: OrganizationStatus): { backgroundColor: string; fontColor: string } => {
        switch (label) {
            case OrganizationStatus.REQUESTED:
                return {
                    backgroundColor: BLUISH_WHITE,
                    fontColor: Colors.blue[500],
                };
            case OrganizationStatus.APPROVED:
                return {
                    backgroundColor: GREENISH_WHITE,
                    fontColor: Colors.green[500],
                };
            default:
                return {
                    backgroundColor: Colors.white[400],
                    fontColor: Colors.darkBlack[900],
                };
        }
    };

    return (
        <Box sx={styles.list}>
            {filteredAndSortedOrganizations.map((org) => {
                const tagStyles = getTagStyles(org.organizationStatus);

                return (
                    <SelectableDropdownListItem
                        key={org.organizationId}
                        title={org.organizationName}
                        subtitle={roleText(org.role, t)}
                        onClick={(): void => navigate(`/organizations/${org.organizationId}`)}
                        rightComponent={
                            org.organizationStatus !== OrganizationStatus.CLAIMED ? (
                                <ListItemTag
                                    data-testid={`${org.organizationStatus}-organizationStatus-tag`}
                                    margin={2}
                                    label={org.organizationStatus}
                                    backgroundColor={tagStyles.backgroundColor}
                                    fontWeight={'bold'}
                                    fontColor={tagStyles.fontColor}
                                />
                            ) : undefined
                        }
                        leftComponent={
                            <Box sx={styles.leftComponent}>
                                <Business />
                            </Box>
                        }
                    />
                );
            })}
        </Box>
    );
};
